import { useEffect, useMemo, useState, type ReactNode } from 'react'
import './Playoffs.css'
import './BracketPlay.css'
import api from '../services/api'
import { PLAYOFF_ROUND_1_SERIES, PLAYOFF_ROUND_2_SERIES, PLAYOFF_ROUND_3_SERIES, PLAYOFF_CHAMPION_TEAM } from '../constants/playoffs'
import { getLeagueTeamDisplayName } from '../constants/teamNames'
import {
  buildMatchupResultsMap,
  buildUserByTeamAndRound,
  computePlayoffSeriesWins,
  getBracketLeaderSlotDetails,
  getPlayoffChampionDisplayName,
  type PlayoffCompetitorInput,
  type PlayoffGameInput,
  type SeriesWinsTally,
} from '../lib/playoffSeriesStats'

interface BracketSlot {
  label: string
  detail: string
  status: 'seed' | 'bye' | 'placeholder' | 'winner'
}

interface BracketMatchup {
  id: string
  slots: [BracketSlot, BracketSlot]
}

const REGULATOR_TEAM_LABEL = getLeagueTeamDisplayName('Team Regulator')
const EMPTY_BRACKET_DETAIL = ''

/**
 * Bracket shell with first-round series driven by the same pairings as the Playoffs schedule page.
 * Series records update from uploaded game results.
 */
export default function BracketPlay() {
  const [users, setUsers] = useState<PlayoffCompetitorInput[]>([])
  const [games, setGames] = useState<PlayoffGameInput[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true)
        const [usersResponse, gamesResponse] = await Promise.all([
          api.get<PlayoffCompetitorInput[]>('/users'),
          api.get<PlayoffGameInput[]>('/games'),
        ])
        setUsers(usersResponse.data ?? [])
        setGames(gamesResponse.data ?? [])
        setError(null)
      } catch (err: unknown) {
        console.error('Failed to load bracket data:', err)
        setError('Failed to load bracket data.')
      } finally {
        setLoading(false)
      }
    }

    void load()
  }, [])

  const seriesTallyByPairingId = useMemo(() => {
    const userByTeamAndRound = buildUserByTeamAndRound(users)
    const matchupResults = buildMatchupResultsMap(games)
    const out = new Map<string, SeriesWinsTally>()

    for (const pairing of PLAYOFF_ROUND_1_SERIES) {
      const tally = computePlayoffSeriesWins(pairing, userByTeamAndRound, matchupResults, users)
      out.set(pairing.id, tally)
    }

    for (const pairing of PLAYOFF_ROUND_2_SERIES) {
      const tally = computePlayoffSeriesWins(pairing, userByTeamAndRound, matchupResults, users)
      out.set(pairing.id, tally)
    }

    for (const pairing of PLAYOFF_ROUND_3_SERIES) {
      const tally = computePlayoffSeriesWins(pairing, userByTeamAndRound, matchupResults, users)
      out.set(pairing.id, tally)
    }

    return out
  }, [users, games])

  const firstRoundMatchups = useMemo((): BracketMatchup[] => {
    const openingSeriesMatchups: BracketMatchup[] = PLAYOFF_ROUND_1_SERIES.map((pairing, index) => {
      const tally = seriesTallyByPairingId.get(pairing.id) ?? emptyTally()
      const [upperDetail, lowerDetail] = getBracketLeaderSlotDetails(tally)

      return {
        id: `quarterfinal-${index + 2}`,
        slots: [
          { label: getLeagueTeamDisplayName(pairing.teamA), detail: upperDetail, status: 'placeholder' },
          { label: getLeagueTeamDisplayName(pairing.teamB), detail: lowerDetail, status: 'placeholder' },
        ],
      }
    })

    return [
      {
        id: 'quarterfinal-1',
        slots: [
          { label: REGULATOR_TEAM_LABEL, detail: EMPTY_BRACKET_DETAIL, status: 'placeholder' },
          { label: 'Bye', detail: EMPTY_BRACKET_DETAIL, status: 'placeholder' },
        ],
      },
      ...openingSeriesMatchups,
    ]
  }, [seriesTallyByPairingId])

  const semifinalMatchups = useMemo((): BracketMatchup[] => {
    return PLAYOFF_ROUND_2_SERIES.map(series => {
      const tally = seriesTallyByPairingId.get(series.id) ?? emptyTally()
      const [upperDetail, lowerDetail] = getBracketLeaderSlotDetails(tally)

      return {
        id: series.id,
        slots: [
          {
            label: getLeagueTeamDisplayName(series.teamA),
            detail: upperDetail,
            status: 'placeholder',
          },
          {
            label: getLeagueTeamDisplayName(series.teamB),
            detail: lowerDetail,
            status: 'placeholder',
          },
        ],
      }
    })
  }, [seriesTallyByPairingId])

  const championshipMatchup: BracketMatchup = useMemo(() => {
    const series = PLAYOFF_ROUND_3_SERIES[0]
    const tally = seriesTallyByPairingId.get(series.id) ?? emptyTally()
    const [upperDetail, lowerDetail] = getBracketLeaderSlotDetails(tally)
    const championTeam = PLAYOFF_CHAMPION_TEAM

    return {
      id: series.id,
      slots: [
        {
          label: getLeagueTeamDisplayName(series.teamA),
          detail: upperDetail,
          status: championTeam === series.teamA ? 'winner' : 'placeholder',
        },
        {
          label: getLeagueTeamDisplayName(series.teamB),
          detail: lowerDetail,
          status: championTeam === series.teamB ? 'winner' : 'placeholder',
        },
      ],
    }
  }, [seriesTallyByPairingId])

  const championLabel = useMemo(() => {
    const series = PLAYOFF_ROUND_3_SERIES[0]
    const tally = seriesTallyByPairingId.get(series.id) ?? emptyTally()
    return getPlayoffChampionDisplayName(
      series,
      tally,
      PLAYOFF_CHAMPION_TEAM,
      getLeagueTeamDisplayName
    )
  }, [seriesTallyByPairingId])

  if (loading) {
    return (
      <div className="playoffs-page">
        <p className="playoffs-subtitle">Loading bracket…</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="playoffs-page">
        <p className="playoffs-subtitle">{error}</p>
      </div>
    )
  }

  return (
    <div className="playoffs-page playoffs-page--bracket">
      <div className="playoffs-hero">
        <p className="playoffs-kicker">Tournament Bracket</p>
        <h1>Bracket Play</h1>
      </div>

      <div className="playoffs-bracket-tree" aria-label="Playoff bracket">
        <BracketColumn title="First Round">
          <BracketFeedGroup variant="pair">
            <BracketMatchupCard matchup={firstRoundMatchups[0]} />
            <BracketMatchupCard matchup={firstRoundMatchups[1]} />
          </BracketFeedGroup>
          <BracketFeedGroup variant="pair">
            <BracketMatchupCard matchup={firstRoundMatchups[2]} />
            <BracketMatchupCard matchup={firstRoundMatchups[3]} />
          </BracketFeedGroup>
        </BracketColumn>

        <BracketColumn title="Semifinals">
          <BracketFeedGroup variant="single">
            <BracketMatchupCard matchup={semifinalMatchups[0]} />
          </BracketFeedGroup>
          <BracketFeedGroup variant="single">
            <BracketMatchupCard matchup={semifinalMatchups[1]} />
          </BracketFeedGroup>
        </BracketColumn>

        <BracketColumn title="Championship" className="bracket-column--championship">
          <BracketFeedGroup variant="single">
            <BracketMatchupCard matchup={championshipMatchup} />
          </BracketFeedGroup>
        </BracketColumn>

        <BracketColumn title="Champion" titleVariant="champion" className="bracket-column--champion">
          <BracketFeedGroup variant="single">
            <article
              className="bracket-matchup-card"
              aria-label={championLabel ? `Champion ${championLabel}` : 'Champion TBD'}
            >
              <div className={`bracket-team-row ${championLabel ? 'winner' : 'placeholder'}`}>
                <span className="bracket-team-label">{championLabel ?? 'TBD'}</span>
              </div>
            </article>
          </BracketFeedGroup>
        </BracketColumn>
      </div>
    </div>
  )
}

function emptyTally(): SeriesWinsTally {
  return {
    teamAWins: 0,
    teamBWins: 0,
    teamALosses: 0,
    teamBLosses: 0,
    ties: 0,
  }
}

function BracketColumn({
  title,
  titleVariant = 'round',
  className = '',
  children,
}: {
  title: string
  titleVariant?: 'round' | 'champion'
  className?: string
  children: ReactNode
}) {
  return (
    <section className={`bracket-column ${className}`.trim()}>
      <h2
        className={
          titleVariant === 'champion'
            ? 'bracket-column-title bracket-column-title--champion'
            : 'bracket-column-title'
        }
      >
        {title}
      </h2>
      <div className="bracket-column-body">{children}</div>
    </section>
  )
}

function BracketFeedGroup({
  variant,
  children,
}: {
  variant: 'pair' | 'single'
  children: ReactNode
}) {
  return <div className={`bracket-feed bracket-feed--${variant}`}>{children}</div>
}

function BracketMatchupCard({ matchup }: { matchup: BracketMatchup }) {
  return (
    <article className="bracket-matchup-card" aria-label={matchup.slots.map(slot => slot.label).join(' versus ')}>
      {matchup.slots.map((slot, slotIndex) => (
        <div
          key={`${matchup.id}-${slotIndex}-${slot.label}`}
          className={`bracket-team-row ${slot.status}`}
        >
          <span className="bracket-team-label">{slot.label}</span>
          {slot.detail ? <span className="bracket-team-record">{slot.detail}</span> : null}
        </div>
      ))}
    </article>
  )
}
